<template>
	<div
		ref="ref__dropdown"
		class="container__dropdown"
		:style="{ width: widthAuto ? 'auto' : '100%' }"
		data-testid="dropdown-component"
	>
		<div
			v-show="label"
			class="container__dropdown__label"
		>
			<div class="flex gap-2 items-center">
				<label>{{ label }}</label>
				<slot name="tooltip" />
			</div>
			<div>
				<i :class="iconLabelDropdown" />
				<label class="container__dropdown__label__icon_desc">{{ textAfterIcon }}</label>
			</div>
		</div>
		<div
			class="container__dropdown__component"
			:class="[{ disabled }, help.type]"
		>
			<label
				:ref="`ref__dropdown__default_${uniqueRef}`"
				class="container__dropdown__component__default"
				@click="switchDropdown"
			>
				<p class="label__current_label">{{ atualLabel }}</p>
				<div class="left__sided_info">
					<i :class="['fas fa-angle-down', { active: showDropdown }]" />
				</div>
			</label>
			<ul
				:ref="`ref__dropdown__list_${uniqueRef}`"
				v-closable="{
					exclude: [`ref__dropdown__list_${uniqueRef}`, `ref__dropdown__default_${uniqueRef}`],
					handler: 'closeDropdown',
				}"
				data-testid="ulOpen-component"
				:class="[
					'container__dropdown__component__list',
					{ 'open--dropdown': showDropdown },
					dropdownDirection,
				]"
			>
				<li :class="['container__dropdown__component__list__item']">
					{{ placeHolder ? placeHolder : $t('selectAnOption') }}
				</li>
				<li
					v-for="(option, index) in options.filter((option) => !option.hide)"
					:id="option.value"
					:key="index"
					:class="[
						{ 'opacity-40 !cursor-not-allowed hover:!bg-transparent': option.disabled },
						'container__dropdown__component__list__item',
						{ 'opacity-40 hover:!bg-transparent !cursor-not-allowed': option.disabled },
					]"
					@click="option.disabled ? () => {} : setValue($event)"
				>
					{{ option.label }}
				</li>
			</ul>
		</div>
		<span
			v-show="Object.entries(help).length > 0"
			class="message__help__dropdown"
			>{{ help.message }}</span
		>
	</div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'

export default {
	name: 'Dropdown',

	props: {
		/**
		 * mensagem de ajuda que pode ser mostrada abaixo dropdown
		 */
		help: {
			type: Object,
			default: () => {
				return { message: '', type: '' }
			},
			required: false,
		},
		/**
		 * Label do que estará escrito acima do componente de Dropdown
		 */
		label: {
			type: String,
			default: '',
		},
		/**
		 * Opções contendo uma lista de value e label para cada opção do dropdown
		 */
		options: { type: Array, default: () => [] },
		/**
		 * Valor selecionado
		 */
		value: {
			type: [String, Array, Number],
		},
		/**
		 * Permite seleção múltipla de opções
		 */
		multiple: {
			type: Boolean,
			default: false,
		},

		disabled: {
			type: Boolean,
			default: false,
		},

		/**
		 * Icone para ser exibido antes da label de dropdown
		 */
		iconLabelDropdown: {
			type: String,
		},

		/**
		 *
		 */
		textAfterIcon: {
			type: String,
		},

		widthAuto: {
			type: Boolean,
			default: false,
		},

		placeHolder: {
			type: String,
		},
	},
	emits: ['update:modelValue'],

	data() {
		return {
			/**
			 * Define a direcao que sera aberto o dropdown, para baixo ou para cima
			 */
			dropdownDirection: 'downward',
			/**
			 * Deve mostrar ou não o dropdown
			 */
			showDropdown: false,
			/**
			 * Valores selecionados pelo usuário
			 */
			values: [],

			/**
			 * Valor selecionado
			 */
			selectedOption: { value: this.$t('selectAnOption') },
		}
	},
	computed: {
		/**
		 * Retorna o label que deve ser exibido dentro do dropdown
		 */
		atualLabel() {
			const semOpcaoSelecionada = this.placeHolder ? this.placeHolder : this.$t('selectAnOption')

			return this.selectedOption?.label ?? semOpcaoSelecionada
		},
		/**
		 * Retorna um Guid para o componente ter como referencia
		 * um valor unico por componente
		 */
		uniqueRef() {
			return uuidv4()
		},
	},

	watch: {
		value(value) {
			if (typeof value === 'string') {
				const option = this.options.find((o) => o.value === value)
				this.selectedOption = option
			}
		},
	},

	created() {
		const optionSelected = this.options.find((f) => f.value === this.value)
		if (optionSelected) this.selectedOption = optionSelected
	},

	methods: {
		/**
		 * Fecha o dropdown
		 */
		closeDropdown() {
			this.showDropdown = false
		},

		/**
		 * Retorna o label que deve ser exibido no dropdown
		 */
		getLabel(option) {
			return option.label ?? option.value
		},

		/**
		 * Retorna se a opcao passada por parametro e a que esta ativa
		 */
		isActiveOption(value) {
			return this.selectedOption.value === value
		},

		/**
		 * Alterna entre mostrar ou nao o dropdown
		 */
		switchDropdown() {
			if (!this.disabled) {
				this.showDropdown = !this.showDropdown
				if (this.showDropdown) {
					this.setDropdownDirection()
				}
			}
		},

		/**
		 * Seta a direcao que o dropdown vai ser aberto
		 */
		setDropdownDirection() {
			const halfScreen = window.innerHeight / 2
			const elementPosition = this.$refs.ref__dropdown.getBoundingClientRect().top

			if (elementPosition > halfScreen) {
				this.dropdownDirection = 'upward'
			} else {
				this.dropdownDirection = 'downward'
			}
		},

		/**
		 * Seta o valor selecionado
		 */
		setValue(value) {
			const option = this.options.find((o) => o.value === value.target.id)

			this.selectedOption = option
			this.$emit('input', option.value)
			this.$emit('change', option.value)
			this.closeDropdown()
		},
	},
}
</script>

<style scoped>
.left__sided_info {
	display: flex;
	margin-left: 0.5rem;
}

.container__dropdown {
	display: flex;
	flex-direction: column;
	width: 100%;
	user-select: none;
}

.container__dropdown__label {
	color: var(--color-navy-blue);
	font-weight: 400;
	margin-bottom: 0px !important;
	display: flex;
	justify-content: space-between;
}

.container__dropdown__label__icon_desc {
	margin-left: 0.2rem;
	text-decoration: underline;
}

.container__dropdown__label i {
	margin-left: 0.5rem;
}

.container__dropdown__component {
	position: relative;
}

.container__dropdown__component__default {
	border-radius: 4.5px;
	width: 100%;
	display: flex;
	height: 2rem;
	align-items: center;
	color: var(--color-navy-blue);
	font-weight: 400;
	cursor: pointer;
	padding: 0 1rem;
	display: flex;
	font-size: 0.75rem;
	justify-content: space-between;
}

.container__dropdown__component.disabled {
	background-color: lightgray !important;
	cursor: not-allowed !important;
}

.container__dropdown__component__default i {
	transition: transform 0.1s ease-in-out;
}

.container__dropdown__component__default i.active {
	transform: rotate(-180deg);
}

.container__dropdown__component__list {
	position: absolute;
	border-left: 1px solid var(--color-gray);
	border-right: 1px solid var(--color-gray);
	background-color: white;
	width: 100%;
	max-height: 0;
	overflow: hidden;
	overflow-y: auto;
	z-index: 1;
	transition: max-height 0.1s ease-in-out;
	list-style-type: none;
	padding-left: 0;
}

.container__dropdown__component__list.downward {
	border-bottom-left-radius: 5px;
	border-bottom-right-radius: 5px;
	top: 1.9rem;
}

.container__dropdown__component__list.upward {
	bottom: 1.8rem;
	border-top-left-radius: 5px;
	border-top-right-radius: 5px;
}

.container__dropdown__component__list.open--dropdown {
	max-height: 30vh;
}

.container__dropdown__component__list.open--dropdown.downward {
	border-bottom: 1px solid var(--color-gray);
}

.container__dropdown__component__list.open--dropdown.upward {
	border-top: 1px solid var(--color-gray);
}

.container__dropdown__component__list__item {
	padding: 0.3rem 1rem 0.3rem 1rem;
	cursor: pointer;
	font-size: 0.8rem;
	border-bottom: 1px solid rgba(var(--color-neutral), 0.5);
	transition: all 0.1s ease-in-out;
}

.container__dropdown__component__list__item.active {
	background-color: rgba(var(--color-primary), 0.05);
	font-weight: 700;
}

.container__dropdown__component__list__item:hover {
	background-color: rgba(var(--color-primary), 0.1);
}

.label__current_label {
	margin: 0;
}

.message__help__dropdown {
	margin: 0px;
	font-size: 0.7rem;
}
</style>
